import { SITE } from "~/marketing/data/site";
import type { FAQItem } from "~/marketing/data/personas";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.domain,
    description: SITE.description,
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.domain,
  };
}

export function breadcrumbSchema(
  items: { name: string; url: string }[]
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function faqSchema(items: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function articleSchema(opts: {
  title: string;
  description: string;
  url: string;
  datePublished?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.title,
    description: opts.description,
    url: opts.url,
    datePublished: opts.datePublished || "2026-02-24",
    author: {
      "@type": "Organization",
      name: SITE.name,
    },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
    },
  };
}

export function definedTermSchema(opts: {
  term: string;
  definition: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: opts.term,
    description: opts.definition,
    url: opts.url,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "E-Commerce & Merchandising Glossary",
      url: `${SITE.domain}/glossary`,
    },
  };
}

export function webApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Free Shopify Collection Health Audit",
    url: `${SITE.domain}/audit`,
    description:
      "Free tool to scan your Shopify store for out-of-stock products in collections, estimate revenue at risk, and get a health score.",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    creator: {
      "@type": "Organization",
      name: SITE.name,
      url: SITE.domain,
    },
  };
}

export function softwareApplicationSchema() {
  const base: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE.name,
    description: SITE.description,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Shopify",
    url: SITE.appInstallUrl,
    offers: [
      { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
      { "@type": "Offer", name: "Pro", price: "29", priceCurrency: "USD" },
      { "@type": "Offer", name: "Growth", price: "99", priceCurrency: "USD" },
    ],
    publisher: { "@type": "Organization", name: SITE.name, url: SITE.domain },
  };
  // GATED: only emit a rating when explicitly enabled AND real values exist.
  // No placeholder stars (Google structured-data policy).
  const aso = SITE.aso as { showRating: boolean; ratingValue?: number; reviewCount?: number };
  if (aso.showRating && aso.ratingValue && aso.reviewCount) {
    base.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: aso.ratingValue,
      reviewCount: aso.reviewCount,
    };
  }
  return base;
}
